/**
 * LinguistPro — Application Entry Point
 * Sets up routing, auth listener, and root render.
 *
 * SPEC-023: Heavy admin/profile pages are lazy-loaded to reduce main bundle.
 * Recharts (used in admin metrics/dashboard) moves to its own chunk.
 */

import React, { useEffect, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import AuthPage from './pages/AuthPage';
import LandingPage from './pages/LandingPage';
import SupportNewPage from './pages/SupportNewPage';
import SupportMyTicketsPage from './pages/SupportMyTicketsPage';
import { ErrorBoundary, ProtectedRoute, AppLayout, OnboardingModal } from './components';
import ExternalRedirect from './components/ExternalRedirect';
import { HELP_URL, TERMS_URL, PRIVACY_URL } from './lib/legalUrls';
import PaymentModal from './components/payment/PaymentModal';
import { useAuthStore } from './store/useAuthStore';
import { supabase } from './lib/supabase';
import { installChunkErrorRecovery, lazyRouteImport } from './lib/chunkReload';
import './index.css';

// ── Lazy-loaded routes (reduces initial bundle; Recharts moves to admin chunk) ─

const ProfilePage         = React.lazy(lazyRouteImport(() => import('./pages/ProfilePage')));
const SettingsPage        = React.lazy(lazyRouteImport(() => import('./pages/SettingsPage')));
const TranscriptionsPage  = React.lazy(lazyRouteImport(() => import('./pages/TranscriptionsPage')));
const AdminDashboardPage  = React.lazy(lazyRouteImport(() => import('./pages/admin/DashboardPage')));
const AdminUsersPage      = React.lazy(lazyRouteImport(() => import('./pages/admin/UsersPage')));
const AdminMetricsPage    = React.lazy(lazyRouteImport(() => import('./pages/admin/MetricsPage')));
const AdminTicketsPage    = React.lazy(lazyRouteImport(() => import('./pages/admin/TicketsPage')));
const AdminCouponsPage    = React.lazy(lazyRouteImport(() => import('./pages/admin/CouponsPage')));
const OrgPage             = React.lazy(lazyRouteImport(() => import('./pages/OrgPage')));

// ── Page-load spinner shown while lazy chunks download ────────────────────────

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-gray-200 border-t-[#00d4b8] rounded-full animate-spin" />
        <p className="text-sm text-gray-400 font-medium">Loading…</p>
      </div>
    </div>
  );
}

// ── Auth provider ─────────────────────────────────────────────────────────────

function AuthProvider({ children }: { children: React.ReactNode }) {
  const { setSession } = useAuthStore();

  useEffect(() => {
    // Load initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    // Listen for auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
      }
    );

    return () => subscription.unsubscribe();
  }, [setSession]);

  return <>{children}</>;
}

// ── Root ──────────────────────────────────────────────────────────────────────

function Root() {
  return (
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public landing page */}
                <Route path="/" element={<LandingPage />} />
                {/* Auth flow */}
                <Route path="/auth" element={<AuthPage />} />
                {/* Help, Privacy and Terms are published on techsight.co.za/linguistpro/; old paths redirect there */}
                <Route path="/help" element={<ExternalRedirect to={HELP_URL} keepHash />} />
                <Route path="/privacy" element={<ExternalRedirect to={PRIVACY_URL} />} />
                <Route path="/terms" element={<ExternalRedirect to={TERMS_URL} />} />
                {/* Support tickets — auth required */}
                <Route
                  path="/support/new"
                  element={<ProtectedRoute><SupportNewPage /></ProtectedRoute>}
                />
                <Route
                  path="/support/my-tickets"
                  element={<ProtectedRoute><SupportMyTicketsPage /></ProtectedRoute>}
                />
                {/* Admin — email-gated inside the page itself; lazy-loaded */}
                <Route
                  path="/admin/dashboard"
                  element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>}
                />
                <Route
                  path="/admin/users"
                  element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>}
                />
                <Route
                  path="/admin/metrics"
                  element={<ProtectedRoute><AdminMetricsPage /></ProtectedRoute>}
                />
                <Route
                  path="/admin/tickets"
                  element={<ProtectedRoute><AdminTicketsPage /></ProtectedRoute>}
                />
                <Route
                  path="/admin/coupons"
                  element={<ProtectedRoute><AdminCouponsPage /></ProtectedRoute>}
                />
                {/* Profile — lazy-loaded */}
                <Route
                  path="/profile"
                  element={<ProtectedRoute><ProfilePage /></ProtectedRoute>}
                />
                {/* Settings — lazy-loaded */}
                <Route
                  path="/settings"
                  element={<ProtectedRoute><SettingsPage /></ProtectedRoute>}
                />
                {/* Transcription history — lazy-loaded */}
                <Route
                  path="/transcriptions"
                  element={<ProtectedRoute><TranscriptionsPage /></ProtectedRoute>}
                />
                {/* Organisation dashboard — lazy-loaded */}
                <Route
                  path="/org"
                  element={<ProtectedRoute><OrgPage /></ProtectedRoute>}
                />
                {/* Protected app */}
                <Route
                  path="/app"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <OnboardingModal />
                        <PaymentModal />
                        <App />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />
                {/* Catch-all: send unknown paths to landing */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );
}

installChunkErrorRecovery();

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Could not find root element to mount to');

ReactDOM.createRoot(rootElement).render(<Root />);
